/**
 * SyproFlow Voucher Preview -- F3 Add/Delete Row
 * ================================================
 * Fetches mapper_results from /api/v1/preview/tally-voucher
 * and renders voucher info + ledger entries in a compact
 * spreadsheet grid (# | Ledger Name | Debit | Credit | del).
 *
 * Amount cells are editable inputs (F2a). Ledger-name cells are
 * autocomplete pickers (F2b). Header fields (party, invoice #,
 * date, narration) are editable text inputs (F2c) wired through
 * to both bridge push and cloud audit payloads. Voucher type is
 * a constrained <select> dropdown (7 canonical types).
 *
 * F3: "+ Add row" button inside scroll area after last row.
 * Delete column (narrow trailing column with Trash2 icon) on
 * every row. computeUnsavedEdits handles count changes.
 *
 * Date field: non-blocking format warning if edited value doesn't
 * look like DD-MM-YYYY; non-blocking future-date warning on ANY
 * displayed date (edited or AI-extracted). Push is NOT blocked.
 *
 * Amber audit dot on changed rows (amount, dr_cr, or ledger_name).
 * Cancel-confirmation detects both entry and header edits.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { computeBalance, computeUnsavedEdits, computeUnsavedHeaderEdits } from './voucherBalance';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.SYPROFLOW_CONFIG?.API_BASE_URL) {
    return window.SYPROFLOW_CONFIG.API_BASE_URL;
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('syproflow_api_base');
    if (stored !== null) return stored;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return 'http://localhost:8000';
};

const API_BASE = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('syproflow_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const formatAmount = (amount) => {
  const num = Number(amount) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Permissive date pattern: D(D) sep M(M) sep YY(YY) where sep is - / or .
const DATE_PATTERN = /^\d{1,2}[-\/.]\d{1,2}[-\/.]\d{2,4}$/;

// Parse a DD-MM-YYYY (or D-M-YY with any separator) string into a Date.
// Returns null if unparseable. Handles 2-digit years (00-49 -> 2000s, 50-99 -> 1900s).
function parseDateDMY(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.trim().match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{2,4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  let year = parseInt(m[3], 10);
  if (year < 100) year += year < 50 ? 2000 : 1900;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  // Validate the date didn't roll over (e.g. Feb 30 -> Mar 2)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

// The 7 canonical voucher types the bridge supports.
// Unknown types silently default to Purchase in the bridge -- so we constrain the UI.
const VOUCHER_TYPES = ['Purchase', 'Sales', 'Credit Note', 'Debit Note', 'Payment', 'Receipt', 'Journal'];

// Normalize raw voucher-type strings to canonical form, mirroring the bridge's
// VOUCHER_TYPE_MAP (voucher_generator.py lines 195-216).
const VOUCHER_TYPE_NORMALIZE = {
  purchase: 'Purchase', purchase_invoice: 'Purchase', 'purchase invoice': 'Purchase',
  bill: 'Purchase', vendor_invoice: 'Purchase',
  sales: 'Sales', sales_invoice: 'Sales', 'sales invoice': 'Sales', tax_invoice: 'Sales',
  credit_note: 'Credit Note', 'credit note': 'Credit Note', sales_return: 'Credit Note',
  debit_note: 'Debit Note', 'debit note': 'Debit Note', purchase_return: 'Debit Note',
  payment: 'Payment', expense: 'Payment',
  receipt: 'Receipt',
  journal: 'Journal', journal_entry: 'Journal',
};

function normalizeVoucherType(raw) {
  if (!raw) return 'Purchase';
  const key = String(raw).toLowerCase().trim();
  return VOUCHER_TYPE_NORMALIZE[key] || null;
}

// Is the given date string in the future relative to today?
function isFutureDate(str) {
  const parsed = parseDateDMY(str);
  if (!parsed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed > today;
}

export default function VoucherPreview() {
  const { docId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const doc = state?.doc;

  const [mapperResults, setMapperResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editedEntries, setEditedEntries] = useState([]);
  const [editedVoucherInfo, setEditedVoucherInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [ledgerList, setLedgerList] = useState([]);
  const [ledgerStatus, setLedgerStatus] = useState('loading');
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/v1/preview/tally-voucher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          tables: doc.tables || [],
          accounting: doc.accounting || {},
          metadata: doc.metadata || {},
          document_name: doc.name || 'Document',
          tally_ledgers: ledgerList,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Preview failed (${response.status})`);
      }
      const result = await response.json();
      const fetched = result.mapper_results || [];
      setMapperResults(fetched);
      setEditedEntries((fetched[0]?.ledger_entries || []).map(e => ({ ...e })));
      const vi = fetched[0]?.voucher_info || {};
      const rawType = fetched[0]?.voucher_type || fetched[0]?.document_type || '';
      setEditedVoucherInfo({
        party_name: vi.party_name || '',
        voucher_number: vi.voucher_number || '',
        date: vi.date || '',
        narration: vi.narration || '',
        voucher_type: normalizeVoucherType(rawType) || '',
      });
    } catch (err) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an entry's amount. Model A (auto-clear):
   * - Typing into Debit sets dr_cr='Dr', clears the Credit column.
   * - Typing into Credit sets dr_cr='Cr', clears the Debit column.
   * - Negatives are disallowed (clamped to 0).
   */
  const updateEntryAmount = (idx, column, rawValue) => {
    let cleaned = rawValue.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
    const num = cleaned === '' || cleaned === '.' ? 0 : parseFloat(cleaned);
    const finalAmount = Math.max(0, num);

    setEditedEntries(prev =>
      prev.map((entry, i) => {
        if (i !== idx) return entry;
        return {
          ...entry,
          amount: finalAmount,
          dr_cr: column === 'dr' ? 'Dr' : 'Cr',
        };
      })
    );
  };

  const updateEntryLedger = (idx, newName) => {
    setEditedEntries(prev =>
      prev.map((entry, i) => {
        if (i !== idx) return entry;
        return { ...entry, ledger_name: newName };
      })
    );
  };

  const addRow = () => {
    setEditedEntries(prev => [...prev, { ledger_name: '', amount: 0, dr_cr: 'Dr' }]);
  };

  const deleteRow = (idx) => {
    setEditedEntries(prev => prev.filter((_, i) => i !== idx));
    setPendingDeleteIdx(null);
  };

  const handleApprove = async () => {
    setSubmitting(true);
    setPushError(null);
    setDuplicateInfo(null);

    let auditId;

    // Step 1: Cloud apply (audit log + diff)
    // Override both ledger_entries AND voucher_info so header edits
    // reach the server-side _compute_voucher_diff audit.
    try {
      const editedMapperResults = mapperResults.map((mr, i) =>
        i === 0 ? {
          ...mr,
          ledger_entries: editedEntries,
          voucher_info: { ...(mr.voucher_info || {}), ...editedVoucherInfo },
        } : mr
      );
      const applyRes = await fetch(`${API_BASE}/api/v1/apply/tally-voucher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          original_mapper_results: mapperResults,
          edited_mapper_results: editedMapperResults,
          document_name: doc.name || 'Document',
        }),
      });
      if (!applyRes.ok) {
        const errData = await applyRes.json().catch(() => ({}));
        throw new Error(errData.detail || `Apply failed (${applyRes.status})`);
      }
      const applyResult = await applyRes.json();
      auditId = applyResult.audit_id;
    } catch (err) {
      setPushError(err.message || 'Apply failed');
      setSubmitting(false);
      return;
    }

    // Step 2: Bridge status check (captures active company for safety gate)
    let activeCompany = null;
    try {
      const statusRes = await fetch('http://localhost:8765/status', {
        signal: AbortSignal.timeout(3000),
      });
      if (!statusRes.ok) throw new Error('not ok');
      const statusData = await statusRes.json();
      activeCompany = statusData.company || null;
    } catch {
      setPushError('Tally Bridge is not running. Please start it to push this voucher.');
      setSubmitting(false);
      return;
    }

    // Step 3: Bridge push -- reads edited header values
    try {
      const bridgePayload = {
        party_name: editedVoucherInfo.party_name || '',
        voucher_type: editedVoucherInfo.voucher_type || 'Purchase',
        invoice_number: editedVoucherInfo.voucher_number || '',
        invoice_date: editedVoucherInfo.date || '',
        narration: editedVoucherInfo.narration || '',
        expected_company: activeCompany,
        entries: editedEntries.map(e => ({
          ledger: e.ledger_name,
          amount: Number(e.amount) || 0,
          is_debit: e.dr_cr === 'Dr',
        })),
      };
      const pushRes = await fetch('http://localhost:8765/push/approved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bridgePayload),
      });
      const pushResult = await pushRes.json().catch(() => ({}));
      if (!pushRes.ok || !pushResult.success) {
        const code = pushResult.error_code;
        if (code === 'company_mismatch' || code === 'company_unverified' || code === 'tally_unreachable') {
          setPushError(pushResult.message);
          setSubmitting(false);
          return;
        }
        if (code === 'duplicate_voucher' || pushResult.duplicate) {
          const pushedAt = pushResult.previous_push?.pushed_at?.slice(0, 19).replace('T', ' ') || 'unknown';
          setDuplicateInfo({ pushedAt, auditId });
          setSubmitting(false);
          return;
        }
        throw new Error(pushResult.message || `Bridge push failed (${pushRes.status})`);
      }
      setPushSuccess({ audit_id: auditId, bridgeMessage: pushResult.message || '' });
    } catch (err) {
      setPushError(err.message || 'Bridge push failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForcePush = async () => {
    // Capture before clearing state
    const auditId = duplicateInfo?.auditId;
    setSubmitting(true);
    setDuplicateInfo(null);
    setPushError(null);

    // Step 2 (re-run): Bridge status check — company gate must still apply
    let activeCompany = null;
    try {
      const statusRes = await fetch('http://localhost:8765/status', {
        signal: AbortSignal.timeout(3000),
      });
      if (!statusRes.ok) throw new Error('not ok');
      const statusData = await statusRes.json();
      activeCompany = statusData.company || null;
    } catch {
      setPushError('Tally Bridge is not running. Please start it to push this voucher.');
      setSubmitting(false);
      return;
    }

    // Step 3 (re-run): Bridge push with force_push: true
    try {
      const bridgePayload = {
        party_name: editedVoucherInfo.party_name || '',
        voucher_type: editedVoucherInfo.voucher_type || 'Purchase',
        invoice_number: editedVoucherInfo.voucher_number || '',
        invoice_date: editedVoucherInfo.date || '',
        narration: editedVoucherInfo.narration || '',
        expected_company: activeCompany,
        force_push: true,
        entries: editedEntries.map(e => ({
          ledger: e.ledger_name,
          amount: Number(e.amount) || 0,
          is_debit: e.dr_cr === 'Dr',
        })),
      };
      const pushRes = await fetch('http://localhost:8765/push/approved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bridgePayload),
      });
      const pushResult = await pushRes.json().catch(() => ({}));
      if (!pushRes.ok || !pushResult.success) {
        throw new Error(pushResult.message || `Bridge push failed (${pushRes.status})`);
      }
      setPushSuccess({ audit_id: auditId, bridgeMessage: pushResult.message || '' });
    } catch (err) {
      setPushError(err.message || 'Bridge push failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (doc) fetchPreview();
    else setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showCancelModal) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowCancelModal(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCancelModal]);

  // Fetch ledger list from bridge (once on mount, cached for session)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('http://localhost:8765/ledgers', {
          signal: AbortSignal.timeout(3000),
        });
        if (!res.ok) throw new Error('not ok');
        const data = await res.json();
        if (!cancelled) {
          const names = (data.ledgers || []).map(l => l.name).filter(Boolean);
          setLedgerList(names);
          setLedgerStatus(names.length > 0 ? 'ready' : 'unavailable');
        }
      } catch {
        if (!cancelled) setLedgerStatus('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fallback when navigated to directly without route state
  if (!doc) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <p className="text-[15px] text-gray-700 mb-2">No voucher data available</p>
          <p className="text-[13px] text-gray-500 mb-6">
            Could not load preview for document <span className="font-mono text-gray-600">{docId}</span>.
            Please return to the app and select a document.
          </p>
          <button
            onClick={() => navigate('/app')}
            className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </button>
        </div>
      </div>
    );
  }

  const result = mapperResults && mapperResults.length > 0 ? mapperResults[0] : null;
  const voucher = result?.voucher_info || {};
  const originalEntries = result?.ledger_entries || [];

  const { totalDr, totalCr, balanceDiff, isBalanced } = computeBalance(editedEntries);

  // Original normalized type for comparison (voucher_type is top-level, not in voucher_info)
  const originalNormalizedType = normalizeVoucherType(
    result?.voucher_type || result?.document_type || ''
  ) || result?.voucher_type || result?.document_type || 'Purchase';

  const hasUnsavedEdits =
    computeUnsavedEdits(editedEntries, originalEntries, pushSuccess) ||
    computeUnsavedHeaderEdits(editedVoucherInfo, voucher, pushSuccess) ||
    (!pushSuccess && editedVoucherInfo?.voucher_type !== originalNormalizedType);

  // Date format warning: only when date was EDITED and doesn't match permissive pattern
  const dateFormatWarning = editedVoucherInfo &&
    editedVoucherInfo.date !== (voucher.date || '') &&
    editedVoucherInfo.date.trim() !== '' &&
    !DATE_PATTERN.test(editedVoucherInfo.date.trim());

  // Future-date warning: on ANY displayed date (edited or AI-extracted)
  const displayedDate = editedVoucherInfo?.date || voucher.date || '';
  const dateFutureWarning = displayedDate.trim() !== '' && isFutureDate(displayedDate.trim());

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <div className="max-w-4xl mx-auto px-4 py-4">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => {
              if (hasUnsavedEdits) setShowCancelModal(true);
              else navigate(-1);
            }}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Voucher Preview</h1>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-16 mb-4 flex flex-col items-center justify-center">
            <Loader2 className="w-6 h-6 text-orange-600 animate-spin mb-3" />
            <p className="text-[13px] text-gray-500">Loading preview...</p>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="bg-white border border-red-200 rounded-lg px-5 py-8 mb-4">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-6 h-6 text-red-500 mb-3" />
              <p className="text-[14px] text-gray-900 font-medium mb-1">Preview failed</p>
              <p className="text-[13px] text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchPreview}
                className="px-4 py-2 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty results state */}
        {!loading && !error && mapperResults && mapperResults.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-8 mb-4">
            <div className="flex flex-col items-center text-center">
              <p className="text-[14px] text-gray-900 font-medium mb-1">No voucher data extracted</p>
              <p className="text-[13px] text-gray-500">Re-run extraction or check document type.</p>
            </div>
          </div>
        )}

        {/* Success: Voucher Info + Ledger Grid + Actions */}
        {!loading && !error && result && editedVoucherInfo && (
          <>
            {/* Compact Voucher Details Header */}
            <div className="bg-orange-100 border-2 border-orange-400 rounded-lg overflow-hidden mb-3">
              <div className="bg-[#EA580C] px-4 py-1.5">
                <span className="text-white text-[12px] font-semibold uppercase tracking-wide">Voucher Details</span>
              </div>
              <div className="px-4 py-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-[13px]">
                  <div className="flex gap-1.5 items-center">
                    <span className="text-orange-900 font-semibold shrink-0">Party:</span>
                    <HeaderField
                      value={editedVoucherInfo.party_name}
                      onChange={(v) => setEditedVoucherInfo(prev => ({ ...prev, party_name: v }))}
                      className="font-semibold"
                    />
                  </div>
                  <div className="flex gap-1.5 items-start">
                    <span className="text-orange-900 font-semibold shrink-0 mt-px">Type:</span>
                    <div className="flex-1 min-w-0">
                      <select
                        value={editedVoucherInfo.voucher_type}
                        onChange={(e) => setEditedVoucherInfo(prev => ({ ...prev, voucher_type: e.target.value }))}
                        className={`w-full text-[13px] font-semibold px-1 py-0 rounded border bg-transparent cursor-pointer outline-none transition-colors ${
                          editedVoucherInfo.voucher_type
                            ? 'text-gray-900 border-transparent hover:border-orange-300/50 focus:border-orange-400 focus:ring-2 focus:ring-orange-200'
                            : 'text-amber-700 border-amber-300 bg-amber-50 focus:border-orange-400 focus:ring-2 focus:ring-orange-200'
                        }`}
                      >
                        {!editedVoucherInfo.voucher_type && (
                          <option value="" disabled>
                            {(result?.voucher_type || result?.document_type || 'Unknown')} — select type
                          </option>
                        )}
                        {VOUCHER_TYPES.map(vt => (
                          <option key={vt} value={vt}>{vt}</option>
                        ))}
                      </select>
                      {!editedVoucherInfo.voucher_type && (
                        <div className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 mt-0.5">
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          Unrecognized voucher type {'\u2014'} please select one
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 items-start">
                    <span className="text-orange-900 font-semibold shrink-0 mt-px">Date:</span>
                    <div className="flex-1 min-w-0">
                      <HeaderField
                        value={editedVoucherInfo.date}
                        onChange={(v) => setEditedVoucherInfo(prev => ({ ...prev, date: v }))}
                        className="font-semibold"
                      />
                      {dateFormatWarning && (
                        <div className="text-[10px] text-amber-600 px-1 mt-0.5">
                          Check date format {'\u2014'} expected DD-MM-YYYY
                        </div>
                      )}
                      {dateFutureWarning && (
                        <div className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 mt-0.5">
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          Date is in the future {'\u2014'} please check
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <span className="text-orange-900 font-semibold shrink-0">Invoice #:</span>
                    <HeaderField
                      value={editedVoucherInfo.voucher_number}
                      onChange={(v) => setEditedVoucherInfo(prev => ({ ...prev, voucher_number: v }))}
                      className="font-semibold"
                    />
                  </div>
                  <div className="sm:col-span-2 flex gap-1.5 items-center">
                    <span className="text-orange-900 font-semibold shrink-0">Narration:</span>
                    <HeaderField
                      value={editedVoucherInfo.narration}
                      onChange={(v) => setEditedVoucherInfo(prev => ({ ...prev, narration: v }))}
                      className="font-medium"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Ledger Grid */}
            <div className="bg-orange-100 border-2 border-orange-400 rounded-lg mb-3 overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto relative bg-white">
                <table className="w-full text-[13px] border-collapse table-fixed">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#EA580C] border-b border-orange-700">
                      <th className="w-8 text-center px-2 py-2 font-semibold text-white/70 text-[12px]">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-white">Ledger Name</th>
                      <th className="text-right px-3 py-2 font-semibold text-white w-[130px]">Debit ({'\u20B9'})</th>
                      <th className="text-right px-3 py-2 font-semibold text-white w-[130px]">Credit ({'\u20B9'})</th>
                      <th className="w-9 px-1 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedEntries.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                          Ledger entries will appear here
                        </td>
                      </tr>
                    ) : (
                      editedEntries.map((entry, idx) => {
                        const original = originalEntries[idx];
                        const wasChanged = original && (
                          entry.dr_cr !== original.dr_cr ||
                          (Number(entry.amount) || 0) !== (Number(original.amount) || 0) ||
                          entry.ledger_name !== original.ledger_name
                        );
                        const isNewLedger = ledgerStatus === 'ready'
                          && entry.ledger_name
                          && entry.ledger_name.trim()
                          && !ledgerList.some(n => n.toLowerCase() === entry.ledger_name.trim().toLowerCase());
                        const needsVerify = typeof entry.confidence === 'number' && entry.confidence < 0.8;
                        const isDr = entry.dr_cr === 'Dr';
                        return (
                          <tr
                            key={idx}
                            className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                          >
                            {/* Row number */}
                            <td className="w-8 text-center px-2 py-1.5 text-[12px] text-gray-600 select-none">
                              {idx + 1}
                            </td>
                            {/* Ledger name picker + audit dot */}
                            <td className="px-1 py-0.5">
                              <div className="flex items-center">
                                <LedgerPicker
                                  value={entry.ledger_name}
                                  ledgerList={ledgerList}
                                  ledgerStatus={ledgerStatus}
                                  onChange={(name) => updateEntryLedger(idx, name)}
                                />
                                {needsVerify && !isNewLedger && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 border border-orange-300 rounded ml-1 shrink-0 leading-none"
                                    title="Low-confidence mapping — please verify this ledger is correct"
                                  >
                                    Verify
                                  </span>
                                )}
                                {isNewLedger && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-300 rounded ml-1 shrink-0 leading-none"
                                    title="This ledger does not exist in Tally and will be created on push"
                                  >
                                    New
                                  </span>
                                )}
                                {wasChanged && (
                                  <span
                                    className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full ml-1 shrink-0"
                                    title="Edited"
                                  />
                                )}
                              </div>
                            </td>
                            {/* Debit column - editable */}
                            <td className="px-1 py-0.5 w-[130px]">
                              <AmountInput
                                value={isDr ? entry.amount : 0}
                                active={isDr}
                                onChange={(val) => updateEntryAmount(idx, 'dr', val)}
                              />
                            </td>
                            {/* Credit column - editable */}
                            <td className="px-1 py-0.5 w-[130px]">
                              <AmountInput
                                value={!isDr ? entry.amount : 0}
                                active={!isDr}
                                onChange={(val) => updateEntryAmount(idx, 'cr', val)}
                              />
                            </td>
                            {/* Delete row */}
                            <td className="w-9 px-1 py-0.5 text-center">
                              <button
                                onClick={() => setPendingDeleteIdx(idx)}
                                className={`p-1 rounded transition-colors ${
                                  pendingDeleteIdx === idx
                                    ? 'text-red-500 bg-red-50'
                                    : 'text-gray-300 hover:text-red-500 hover:bg-red-50'
                                }`}
                                title={pendingDeleteIdx === idx ? 'Delete pending — confirm below' : 'Delete row'}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pending-delete confirmation banner */}
              {pendingDeleteIdx !== null && editedEntries[pendingDeleteIdx] && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-y border-red-300 text-[13px] text-red-800">
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                  <span className="flex-1 min-w-0 truncate">
                    Delete row {pendingDeleteIdx + 1}: <strong>{editedEntries[pendingDeleteIdx].ledger_name || '(blank)'}</strong>
                    {' '}{'\u20B9'}{formatAmount(editedEntries[pendingDeleteIdx].amount)}?
                  </span>
                  <button
                    onClick={() => deleteRow(pendingDeleteIdx)}
                    className="px-3 py-1 text-[12px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded transition-colors shrink-0"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setPendingDeleteIdx(null)}
                    className="px-3 py-1 text-[12px] font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded transition-colors shrink-0"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Add row button -- outside scroll area, always visible */}
              <button
                onClick={addRow}
                className="w-full text-left px-3 py-2 text-[13px] text-orange-600 hover:bg-orange-50 hover:text-orange-700 transition-colors font-medium border-t border-gray-100 bg-white"
              >
                + Add row
              </button>
              {/* Total row -- outside scroll area, always visible below grid */}
              {editedEntries.length > 0 && (
                <div className="bg-[#EA580C] text-[13px] font-semibold">
                  <div className="flex items-center">
                    <div className="w-8 px-2 py-2 shrink-0"></div>
                    <div className="flex-1 px-3 py-2 text-white">
                      Total
                      {isBalanced ? (
                        <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-white/90 text-emerald-700 text-[11px] font-semibold">
                          <CheckCircle className="w-3 h-3" />
                          Balanced
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-white/90 text-red-700 text-[11px] font-semibold">
                          <AlertCircle className="w-3 h-3" />
                          Off by {'\u20B9'}{formatAmount(balanceDiff)}
                        </span>
                      )}
                    </div>
                    <div className="w-[130px] px-3 py-2 text-right font-mono tabular-nums text-white shrink-0">
                      {formatAmount(totalDr)}
                    </div>
                    <div className="w-[130px] px-3 py-2 text-right font-mono tabular-nums text-white shrink-0">
                      {formatAmount(totalCr)}
                    </div>
                    <div className="w-9 shrink-0"></div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Gentle hint when fewer than 2 entries */}
        {!loading && !error && result && editedEntries.length < 2 && editedEntries.length > 0 && !pushSuccess && (
          <div className="flex items-center gap-1.5 text-[12px] text-amber-600 mb-2 px-1">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            A voucher needs at least two balanced entries.
          </div>
        )}

        {/* Action Buttons / Push Result */}
        {pushSuccess ? (
          <div className="bg-white border border-emerald-300 rounded-lg px-5 py-5">
            <div className="flex flex-col items-center text-center">
              <CheckCircle className="w-6 h-6 text-emerald-600 mb-2" />
              <p className="text-[14px] text-gray-900 font-medium mb-1">Voucher pushed to Tally</p>
              {pushSuccess.bridgeMessage && (
                <p className="text-[13px] text-gray-500 mb-1">{pushSuccess.bridgeMessage}</p>
              )}
              <p className="text-[12px] text-gray-400 mb-3">
                Audit ID: <span className="font-mono">{pushSuccess.audit_id}</span>
              </p>
              <button
                onClick={() => navigate('/app')}
                className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to App
              </button>
            </div>
          </div>
        ) : duplicateInfo ? (
          <div className="bg-white border border-amber-300 rounded-lg px-5 py-5">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-6 h-6 text-amber-500 mb-2" />
              <p className="text-[14px] text-gray-900 font-medium mb-1">Already pushed</p>
              <p className="text-[13px] text-gray-600 mb-3 max-w-md">
                This invoice was already pushed on {duplicateInfo.pushedAt}. If you deleted it
                from Tally and want to push a corrected version, use Push Anyway. This overrides
                the duplicate-prevention safeguard.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDuplicateInfo(null)}
                  className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForcePush}
                  disabled={submitting}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Pushing...' : 'Push Anyway'}
                </button>
              </div>
            </div>
          </div>
        ) : pushError ? (
          <div className="bg-white border border-red-200 rounded-lg px-5 py-5">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
              <p className="text-[14px] text-gray-900 font-medium mb-1">Push failed</p>
              <p className="text-[13px] text-gray-500 mb-3">{pushError}</p>
              <button
                onClick={handleApprove}
                className="px-4 py-2 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {(() => {
              const verifyCount = editedEntries.filter(
                e => typeof e.confidence === 'number' && e.confidence < 0.8
              ).length;
              if (verifyCount === 0) return null;
              return (
                <div className="mb-2 px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg text-[12px] text-orange-800">
                  <strong>{verifyCount} ledger{verifyCount > 1 ? 's' : ''}</strong> need review — please verify
                  {verifyCount > 1 ? ' these are' : ' this is'} the correct ledger{verifyCount > 1 ? 's' : ''}.
                </div>
              );
            })()}
            {(() => {
              if (ledgerStatus !== 'ready') return null;
              const newLedgers = [...new Set(
                editedEntries
                  .map(e => e.ledger_name?.trim())
                  .filter(n => n && !ledgerList.some(l => l.toLowerCase() === n.toLowerCase()))
              )];
              if (newLedgers.length === 0) return null;
              return (
                <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-[12px] text-amber-800">
                  <strong>{newLedgers.length} new ledger{newLedgers.length > 1 ? 's' : ''}</strong> will be created in Tally:{' '}
                  {newLedgers.map((n, i) => (
                    <span key={n}>{i > 0 ? ', ' : ''}&lsquo;{n}&rsquo;</span>
                  ))}
                  . Confirm these are new, or edit a name to match an existing ledger.
                </div>
              );
            })()}
            <div className="flex items-center justify-end gap-3 mt-1">
              <button
                onClick={() => {
                  if (hasUnsavedEdits) setShowCancelModal(true);
                  else navigate(-1);
                }}
                className="px-5 py-2 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={!isBalanced || submitting || editedEntries.length === 0 || !editedVoucherInfo?.voucher_type}
                title={!isBalanced
                  ? 'Voucher is unbalanced. Total Dr does not equal Total Cr.'
                  : !editedVoucherInfo?.voucher_type
                    ? 'Please select a valid voucher type before pushing.'
                    : submitting
                      ? 'Pushing voucher to Tally...'
                      : ''}
                className={`px-5 py-2 text-[13px] font-medium rounded-lg transition-colors ${
                  !isBalanced || editedEntries.length === 0 || !editedVoucherInfo?.voucher_type
                    ? 'text-gray-500 bg-gray-300 cursor-not-allowed'
                    : submitting
                      ? 'text-white bg-orange-600 opacity-75 cursor-not-allowed'
                      : 'text-white bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Pushing...
                  </span>
                ) : (
                  'Approve and Push'
                )}
              </button>
            </div>
          </>
        )}

      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-modal-title"
          >
            <h2
              id="cancel-modal-title"
              className="text-[16px] font-semibold text-gray-900 mb-2"
            >
              Discard changes?
            </h2>
            <p className="text-[14px] text-gray-600 mb-6">
              Your edits will not be saved.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  navigate(-1);
                }}
                className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Discard
              </button>
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                Keep editing
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/**
 * HeaderField -- editable text input for voucher header fields.
 * Uses dirtyRef so focus-then-blur without typing is a no-op.
 */
function HeaderField({ value, onChange, className = 'font-semibold' }) {
  const inputRef = useRef(null);
  const dirtyRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');

  const handleFocus = () => {
    setLocalValue(value || '');
    dirtyRef.current = false;
    setEditing(true);
    requestAnimationFrame(() => {
      if (inputRef.current) inputRef.current.select();
    });
  };

  const handleBlur = () => {
    setEditing(false);
    if (dirtyRef.current) {
      onChange(localValue);
    }
  };

  const handleChange = (e) => {
    dirtyRef.current = true;
    setLocalValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={editing ? localValue : (value || '')}
      placeholder={'\u2014'}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`flex-1 min-w-0 text-[13px] text-gray-900 px-1 py-0 rounded border outline-none transition-colors ${
        editing
          ? 'border-orange-400 ring-2 ring-orange-200 bg-white'
          : 'border-transparent bg-transparent hover:border-orange-300/50 cursor-text truncate'
      } ${className}`}
      title={!editing ? (value || '') : undefined}
    />
  );
}

/**
 * LedgerPicker -- autocomplete input for ledger names.
 * Suggestions from bridge /ledgers list; portal dropdown avoids
 * overflow clipping. Blur without selection = revert (bridge ready)
 * or commit (bridge down, free-text mode).
 */
function LedgerPicker({ value, ledgerList, ledgerStatus, onChange }) {
  const inputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const selectedRef = useRef(false);
  const [rect, setRect] = useState(null);

  const measureRect = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 250) });
    }
  };

  const handleFocus = () => {
    setQuery(value || '');
    selectedRef.current = false;
    setEditing(true);
    requestAnimationFrame(() => {
      measureRect();
      if (inputRef.current) inputRef.current.select();
    });
  };

  const handleBlur = () => {
    setEditing(false);
    if (ledgerStatus === 'unavailable' && !selectedRef.current) {
      const trimmed = query.trim();
      if (trimmed && trimmed !== value) {
        onChange(trimmed);
      }
    }
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    measureRect();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.target.blur();
    } else if (e.key === 'Enter' && editing) {
      const lq = query.toLowerCase().trim();
      if (lq) {
        const exact = ledgerList.find(n => n.toLowerCase() === lq);
        if (exact) {
          selectedRef.current = true;
          onChange(exact);
        }
      }
      e.target.blur();
    }
  };

  const selectLedger = (name) => {
    selectedRef.current = true;
    onChange(name);
    inputRef.current?.blur();
  };

  const lowerQuery = query.toLowerCase().trim();
  const filtered = editing && lowerQuery
    ? ledgerList.filter(n => n.toLowerCase().includes(lowerQuery))
    : editing ? ledgerList : [];
  const exactMatch = lowerQuery && ledgerList.some(n => n.toLowerCase() === lowerQuery);
  const showCreateNew = editing && lowerQuery && !exactMatch;
  const showDropdown = editing && ledgerStatus === 'ready' && (filtered.length > 0 || showCreateNew);

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={editing ? query : (value || '')}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={`flex-1 min-w-0 w-full text-left text-[13px] px-2 py-1 rounded border transition-colors outline-none ${
          editing
            ? 'border-orange-400 ring-2 ring-orange-200 bg-white text-gray-900'
            : 'border-transparent bg-transparent text-gray-900 hover:border-gray-200 cursor-text truncate'
        }`}
        title={!editing ? (value || '') : undefined}
      />
      {showDropdown && rect && createPortal(
        <div
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filtered.slice(0, 50).map(name => (
            <div
              key={name}
              onMouseDown={(e) => { e.preventDefault(); selectLedger(name); }}
              className="px-3 py-1.5 text-[13px] cursor-pointer hover:bg-orange-50 text-gray-900 truncate"
            >
              {name}
            </div>
          ))}
          {showCreateNew && (
            <div
              onMouseDown={(e) => { e.preventDefault(); selectLedger(query.trim()); }}
              className="px-3 py-1.5 text-[13px] cursor-pointer hover:bg-orange-50 text-orange-600 font-medium border-t border-gray-100"
            >
              + Create new ledger: '{query.trim()}'
            </div>
          )}
        </div>,
        document.body
      )}
      {editing && ledgerStatus === 'unavailable' && rect && createPortal(
        <div
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          }}
          className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm px-3 py-2 text-[11px] text-amber-700"
        >
          Ledger suggestions unavailable {'\u2014'} start Tally Bridge
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * AmountInput -- self-contained editable amount cell.
 * Tracks its own focus state locally (no parent round-trip) so that
 * focus->raw and blur->formatted transitions are synchronous.
 */
function AmountInput({ value, active, onChange }) {
  const inputRef = useRef(null);
  const dirtyRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');

  const handleFocus = () => {
    const num = Number(value) || 0;
    setLocalValue(num > 0 ? String(num) : '');
    dirtyRef.current = false;
    setEditing(true);
    requestAnimationFrame(() => {
      if (inputRef.current) inputRef.current.select();
    });
  };

  const handleBlur = () => {
    setEditing(false);
    if (dirtyRef.current) {
      onChange(localValue);
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (/^[0-9]*\.?[0-9]*$/.test(raw)) {
      dirtyRef.current = true;
      setLocalValue(raw);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  const num = Number(value) || 0;
  const displayValue = editing
    ? localValue
    : num > 0
      ? formatAmount(value)
      : '\u2014';

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`w-full text-right text-[13px] font-mono tabular-nums px-2 py-1 rounded border transition-colors outline-none ${
        editing
          ? 'border-orange-400 ring-2 ring-orange-200 bg-white text-gray-900'
          : active
            ? 'border-transparent bg-transparent text-gray-900 hover:border-gray-200 cursor-text'
            : 'border-transparent bg-transparent text-gray-300 hover:border-gray-200 cursor-text'
      }`}
    />
  );
}
